import { PageHeader } from "@/components/PageHeader";
import { SettingsDialog } from "@/components/SettingsUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type InviteMemberBody,
  type InviteMemberResponse,
  type Me,
  type MemberListEntry,
  type MemberRole,
  type PendingInviteRecord,
  WardkeyAPIError,
  useActiveOrg,
  useApiMutation,
  useApiQuery,
  useOrg,
} from "@/lib/api";
import { useToast } from "@/lib/toast";
import { CircleAlert, Clock3, Mail, Search, ShieldCheck, UserPlus, Users } from "lucide-react";
import { type ReactNode, type RefObject, useId, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";

const ROLES: ReadonlyArray<MemberRole> = ["owner", "admin", "developer", "viewer"];
// Ownership is assigned after joining, through a separate role change.
const INVITABLE_ROLES: ReadonlyArray<MemberRole> = ["developer", "viewer", "admin"];
const ROLE_DETAILS: Record<MemberRole, string> = {
  owner: "Full workspace access, including organization deletion.",
  admin: "Manage teammates, projects, and organization settings.",
  developer: "Read, write, and rotate secrets allowed by policy.",
  viewer: "Read secrets allowed by policy.",
};

export function MembersRoute() {
  const org = useActiveOrg();
  // Close dialogs and discard filters whenever the workspace changes.
  return <MembersWorkspace key={org.org_id} />;
}

function MembersWorkspace() {
  const me = useOutletContext<Me>();
  const org = useActiveOrg();
  const { refresh } = useOrg();
  const navigate = useNavigate();
  const canManage = org.role === "owner" || org.role === "admin";
  const [inviting, setInviting] = useState(false);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const inviteTrigger = useRef<HTMLButtonElement>(null);
  const members = useApiQuery(
    ["members", org.org_id],
    (client) => client.members.list(org.org_id),
    { suppressToast: true },
  );
  const invites = useApiQuery(
    ["invites", org.org_id],
    (client) => client.invites.list(org.org_id),
    { enabled: canManage, suppressToast: true },
  );
  const items = members.data?.members ?? [];
  const pending = invites.data?.invites ?? [];
  const search = query.trim().toLowerCase();
  const matches = (email: string, itemRole: string, name = "") =>
    `${name} ${email}`.toLowerCase().includes(search) && (role === "all" || itemRole === role);
  const visibleMembers = items.filter((item) => matches(item.email, item.role, item.name ?? ""));
  const visibleInvites = pending.filter((item) => matches(item.email, item.role));
  const clearFilters = () => {
    setQuery("");
    setRole("all");
  };

  return (
    <div className="members-page fade-up">
      <PageHeader
        title="Members"
        subtitle={`The people who make ${org.name} work.`}
        actions={
          canManage ? (
            <Button ref={inviteTrigger} type="button" size="sm" onClick={() => setInviting(true)}>
              <UserPlus size={16} aria-hidden="true" />
              Invite member
            </Button>
          ) : undefined
        }
      />
      <div className="members-overview">
        <Overview
          icon={<Users size={19} />}
          label="Workspace members"
          value={members.error ? "Unavailable" : members.data ? String(items.length) : "—"}
        />
        {canManage && (
          <Overview
            icon={<Mail size={19} />}
            label="Pending invitations"
            value={invites.error ? "Unavailable" : invites.data ? String(pending.length) : "—"}
          />
        )}
        <Overview icon={<ShieldCheck size={19} />} label="Your access" value={org.role} />
      </div>
      <div className="members-toolbar">
        <div className="members-search">
          <Search size={17} aria-hidden="true" />
          <input
            aria-label="Search members and invitations"
            placeholder="Search by name or email…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <select
          aria-label="Filter by role"
          value={role}
          onChange={(event) => setRole(event.target.value)}
        >
          <option value="all">All roles</option>
          {ROLES.map((value) => (
            <option key={value} value={value}>
              {value[0]?.toUpperCase()}
              {value.slice(1)}
            </option>
          ))}
        </select>
      </div>
      <section className="members-section" aria-labelledby="members-heading">
        <SectionHeading
          id="members-heading"
          title="Your team"
          description="Everyone with access to this workspace."
          count={members.data ? `${visibleMembers.length} of ${items.length}` : undefined}
        />
        {members.error ? (
          <LoadError title="Couldn't load members" error={members.error} retry={members.refetch} />
        ) : !members.data ? (
          <Loading label="Loading members" />
        ) : visibleMembers.length === 0 ? (
          <Empty
            title={items.length ? "No matching members" : "No members to show"}
            description={
              items.length
                ? "Try another name, email, or role."
                : "Members will appear here when they join this workspace."
            }
            clear={items.length ? clearFilters : undefined}
          />
        ) : (
          <div className="members-list" aria-busy={members.isLoading}>
            <div className="member-columns" aria-hidden="true">
              <span>Member</span>
              <span>Role</span>
              <span>Joined</span>
              <span />
            </div>
            <ul>
              {visibleMembers.map((member) => (
                <MemberRow
                  key={member.user_id}
                  member={member}
                  isSelf={member.user_id === me.user_id}
                  canManage={canManage}
                  orgId={org.org_id}
                  onChanged={members.refetch}
                  onSelfLeft={() => {
                    refresh();
                    navigate("/");
                  }}
                />
              ))}
            </ul>
          </div>
        )}
      </section>
      {canManage && (
        <section className="members-section" aria-labelledby="invitations-heading">
          <SectionHeading
            id="invitations-heading"
            title="Pending invitations"
            description="Teammates who haven't joined yet. Resending replaces the previous invitation link."
            count={invites.data ? `${visibleInvites.length} of ${pending.length}` : undefined}
          />
          {invites.error ? (
            <LoadError
              title="Couldn't load invitations"
              error={invites.error}
              retry={invites.refetch}
            />
          ) : !invites.data ? (
            <Loading label="Loading invitations" />
          ) : visibleInvites.length === 0 ? (
            <Empty
              title={pending.length ? "No matching invitations" : "You're all caught up"}
              description={
                pending.length
                  ? "Try another email or role."
                  : "No invitations are waiting to be accepted."
              }
              clear={pending.length ? clearFilters : undefined}
            />
          ) : (
            <div className="members-list" aria-busy={invites.isLoading}>
              <div className="member-columns invite-columns" aria-hidden="true">
                <span>Invited teammate</span>
                <span>Role</span>
                <span>Status</span>
                <span />
              </div>
              <ul>
                {visibleInvites.map((invite) => (
                  <PendingInviteRow
                    key={invite.invite_id}
                    invite={invite}
                    orgId={org.org_id}
                    onChanged={invites.refetch}
                  />
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
      <details className="members-role-guide">
        <summary>
          <ShieldCheck size={17} aria-hidden="true" />
          Understanding workspace roles
        </summary>
        <dl>
          {ROLES.map((value) => (
            <div key={value}>
              <dt>{value}</dt>
              <dd>{ROLE_DETAILS[value]}</dd>
            </div>
          ))}
        </dl>
      </details>
      {inviting && canManage && (
        <InviteMemberModal
          orgId={org.org_id}
          returnFocus={inviteTrigger}
          onClose={() => setInviting(false)}
          onInvited={(kind) => {
            setInviting(false);
            if (kind === "added") members.refetch();
            else invites.refetch();
          }}
        />
      )}
    </div>
  );
}

function Overview({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="members-overview-item">
      <span aria-hidden="true">{icon}</span>
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}
function SectionHeading({
  id,
  title,
  description,
  count,
}: { id: string; title: string; description: string; count?: string | undefined }) {
  return (
    <header className="members-section-heading">
      <div>
        <h2 id={id}>{title}</h2>
        <p>{description}</p>
      </div>
      {count && <span>{count}</span>}
    </header>
  );
}
function Loading({ label }: { label: string }) {
  return (
    <div className="members-loading" aria-label={label} aria-busy="true">
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  );
}
function LoadError({ title, error, retry }: { title: string; error: Error; retry: () => void }) {
  return (
    <div className="members-error" role="alert">
      <CircleAlert size={22} aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <p>{friendlyError(error)}</p>
        <Button variant="outline" size="sm" onClick={retry}>
          Retry
        </Button>
      </div>
    </div>
  );
}
function Empty({
  title,
  description,
  clear,
}: { title: string; description: string; clear?: (() => void) | undefined }) {
  return (
    <div className="members-empty">
      <Users size={23} aria-hidden="true" />
      <h3>{title}</h3>
      <p>{description}</p>
      {clear && (
        <Button variant="outline" size="sm" onClick={clear}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
function Identity({
  name,
  email,
  self = false,
  invited = false,
}: { name?: string | null; email: string; self?: boolean; invited?: boolean }) {
  const display = name?.trim() || email;
  const words = display.split(/\s+/);
  const initials = (
    words.length > 1 ? `${words[0]?.[0]}${words.at(-1)?.[0]}` : display.slice(0, 2)
  ).toUpperCase();
  return (
    <div className="member-identity">
      <span className={`member-avatar${invited ? " invited" : ""}`} aria-hidden="true">
        {invited ? <Mail size={18} /> : initials}
      </span>
      <div>
        <div className="member-name">
          <strong title={display}>{display}</strong>
          {self && <span className="member-you">you</span>}
        </div>
        {name && name !== email && (
          <span className="member-email" title={email}>
            {email}
          </span>
        )}
      </div>
    </div>
  );
}
function RoleChip({ role }: { role: MemberRole }) {
  return <span className={`member-role member-role-${role}`}>{role}</span>;
}
function RoleSelect({
  value,
  disabled,
  onChange,
  label,
  roles = ROLES,
}: {
  value: MemberRole;
  disabled: boolean;
  onChange: (role: MemberRole) => void;
  label: string;
  roles?: ReadonlyArray<MemberRole>;
}) {
  return (
    <select
      className="member-role-select"
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => {
        const next = event.target.value as MemberRole;
        if (next !== value) onChange(next);
      }}
    >
      {roles.map((role) => (
        <option key={role} value={role}>
          {role[0]?.toUpperCase()}
          {role.slice(1)}
        </option>
      ))}
    </select>
  );
}
function MemberRow({
  member,
  isSelf,
  canManage,
  orgId,
  onChanged,
  onSelfLeft,
}: {
  member: MemberListEntry;
  isSelf: boolean;
  canManage: boolean;
  orgId: string;
  onChanged: () => void;
  onSelfLeft: () => void;
}) {
  const { pushToast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const changeRole = useApiMutation<MemberRole, unknown>(
    (client, role) => client.members.changeRole(orgId, member.user_id, { role }),
    {
      suppressToast: true,
      onSuccess: (_data, role) => {
        pushToast({ kind: "success", title: `Updated ${member.email} to ${role}` });
        onChanged();
      },
      onError: (error) => pushToast({ kind: "error", title: friendlyError(error) }),
    },
  );
  return (
    <li className="member-row">
      <Identity name={member.name} email={member.email} self={isSelf} />
      <div className="member-role-cell">
        {canManage && !isSelf ? (
          <RoleSelect
            value={member.role}
            disabled={changeRole.isPending || confirming}
            onChange={(role) => changeRole.mutate(role)}
            label={`Role for ${member.name || member.email}`}
          />
        ) : (
          <RoleChip role={member.role} />
        )}
      </div>
      <time className="member-date" dateTime={new Date(member.added_at).toISOString()}>
        <span>Joined </span>
        {formatDate(member.added_at)}
      </time>
      <div className="member-actions">
        {(isSelf || canManage) && (
          <Button
            ref={trigger}
            type="button"
            variant="ghost"
            size="sm"
            disabled={changeRole.isPending}
            onClick={() => setConfirming(true)}
          >
            {isSelf ? "Leave" : "Remove"}
          </Button>
        )}
      </div>
      {confirming && (
        <RemoveMemberModal
          member={member}
          isSelf={isSelf}
          orgId={orgId}
          returnFocus={trigger}
          onClose={() => setConfirming(false)}
          onRemoved={() => {
            setConfirming(false);
            if (isSelf) onSelfLeft();
            else onChanged();
          }}
        />
      )}
    </li>
  );
}
function friendlyError(error: Error): string {
  if (error instanceof WardkeyAPIError) {
    if (error.code === "sole_owner")
      return "Can't remove or demote the only owner. Assign another owner first.";
    if (error.code === "forbidden") return "Owner or admin role required.";
    if (error.code === "email_send_failed")
      return "Couldn't deliver the invitation email. Check the address and try again.";
    if (error.code === "invite_expired")
      return "This invitation has expired. Send a new invitation.";
  }
  return error.message || "Something went wrong. Please try again.";
}
function InlineError({ error }: { error: string | null }) {
  return error ? (
    <p className="settings-inline-error" role="alert">
      {error}
    </p>
  ) : null;
}

type DialogProps = {
  orgId: string;
  onClose: () => void;
  returnFocus: RefObject<HTMLButtonElement | null>;
};
function InviteMemberModal({
  orgId,
  onClose,
  onInvited,
  returnFocus,
}: DialogProps & { onInvited: (kind: "added" | "pending") => void }) {
  const { pushToast } = useToast();
  const id = useId();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("developer");
  const [error, setError] = useState<string | null>(null);
  const invite = useApiMutation<InviteMemberBody, InviteMemberResponse>(
    (client, body) => client.members.invite(orgId, body),
    {
      suppressToast: true,
      onSuccess: (data, body) => {
        pushToast({
          kind: "success",
          title:
            data.kind === "added"
              ? `Added ${body.email} as ${body.role}`
              : `Sent invite to ${body.email}`,
        });
        onInvited(data.kind);
      },
      onError: (error) => setError(friendlyError(error)),
    },
  );
  const trimmed = email.trim();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  return (
    <SettingsDialog
      title="Invite a teammate"
      description="Existing Wardkey users join immediately. New teammates receive an email invitation to this workspace."
      onClose={onClose}
      busy={invite.isPending}
      returnFocus={returnFocus}
    >
      <form
        className="member-invite-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid || invite.isPending) return;
          setError(null);
          invite.mutate({ email: trimmed, role });
        }}
      >
        <div className="settings-field">
          <label htmlFor={`${id}-email`}>Email address</label>
          <Input
            id={`${id}-email`}
            type="email"
            required
            maxLength={254}
            autoComplete="email"
            spellCheck={false}
            placeholder="teammate@example.com"
            value={email}
            disabled={invite.isPending}
            onChange={(event) => {
              setEmail(event.target.value);
              setError(null);
            }}
          />
        </div>
        <div className="settings-field">
          <span id={`${id}-role-label`}>Workspace role</span>
          <RoleSelect
            value={role}
            roles={INVITABLE_ROLES}
            disabled={invite.isPending}
            onChange={setRole}
            label="Role"
          />
          <p>{ROLE_DETAILS[role]}</p>
        </div>
        <InlineError error={error} />
        <div className="settings-dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!valid || invite.isPending}>
            {invite.isPending ? "Inviting…" : "Send invite"}
          </Button>
        </div>
      </form>
    </SettingsDialog>
  );
}
function RemoveMemberModal({
  member,
  isSelf,
  orgId,
  onClose,
  onRemoved,
  returnFocus,
}: DialogProps & { member: MemberListEntry; isSelf: boolean; onRemoved: () => void }) {
  const { pushToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const remove = useApiMutation<void, unknown>(
    (client) => client.members.remove(orgId, member.user_id),
    {
      suppressToast: true,
      onSuccess: () => {
        pushToast({
          kind: "success",
          title: isSelf ? "Left organization" : `Removed ${member.email}`,
        });
        onRemoved();
      },
      onError: (error) => setError(friendlyError(error)),
    },
  );
  return (
    <SettingsDialog
      title={isSelf ? "Leave this organization" : "Remove this member"}
      description={
        isSelf
          ? "You'll lose access immediately. To rejoin, an owner or admin must invite you again."
          : "This teammate will lose access immediately. Their existing audit trail stays intact."
      }
      onClose={onClose}
      busy={remove.isPending}
      returnFocus={returnFocus}
    >
      <div className="member-dialog-person">
        <Identity name={member.name} email={member.email} />
        <RoleChip role={member.role} />
      </div>
      <InlineError error={error} />
      <div className="settings-dialog-actions">
        <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={remove.isPending}
          onClick={() => {
            if (remove.isPending) return;
            setError(null);
            remove.mutate();
          }}
        >
          {remove.isPending ? "Working…" : isSelf ? "Leave" : "Remove member"}
        </Button>
      </div>
    </SettingsDialog>
  );
}
function PendingInviteRow({
  invite,
  orgId,
  onChanged,
}: { invite: PendingInviteRecord; orgId: string; onChanged: () => void }) {
  const { pushToast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const expired = invite.expires_at <= Date.now();
  const resend = useApiMutation<void, unknown>(
    (client) => client.invites.resend(orgId, invite.invite_id),
    {
      suppressToast: true,
      onSuccess: () => {
        pushToast({
          kind: "success",
          title: `Re-sent invite to ${invite.email}`,
          body: "The previous link is now invalid; a fresh one was emailed.",
        });
        onChanged();
      },
      onError: (error) => pushToast({ kind: "error", title: friendlyError(error) }),
    },
  );
  return (
    <li className="member-row invite-row">
      <div>
        <Identity email={invite.email} invited />
        <span className="invite-sent">Invited {formatDate(invite.created_at)}</span>
      </div>
      <div className="member-role-cell">
        <RoleChip role={invite.role} />
      </div>
      <div className="invite-status">
        <span className={expired ? "expired" : "pending"}>
          <Clock3 size={12} aria-hidden="true" />
          {expired ? "Expired" : "Pending"}
        </span>
        <time dateTime={new Date(invite.expires_at).toISOString()}>
          {expired ? "Expired" : "Expires"} {formatDate(invite.expires_at)}
        </time>
      </div>
      <div className="member-actions">
        <Button
          variant="ghost"
          size="sm"
          disabled={resend.isPending || confirming}
          onClick={() => {
            if (!resend.isPending) resend.mutate();
          }}
        >
          {resend.isPending ? "Sending…" : "Resend"}
        </Button>
        <Button
          ref={trigger}
          variant="ghost"
          size="sm"
          disabled={resend.isPending}
          onClick={() => setConfirming(true)}
        >
          Revoke
        </Button>
      </div>
      {confirming && (
        <RevokeInviteModal
          invite={invite}
          orgId={orgId}
          returnFocus={trigger}
          onClose={() => setConfirming(false)}
          onRevoked={() => {
            setConfirming(false);
            onChanged();
          }}
        />
      )}
    </li>
  );
}
function RevokeInviteModal({
  invite,
  orgId,
  onClose,
  onRevoked,
  returnFocus,
}: DialogProps & { invite: PendingInviteRecord; onRevoked: () => void }) {
  const { pushToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const revoke = useApiMutation<void, unknown>(
    (client) => client.invites.revoke(orgId, invite.invite_id),
    {
      suppressToast: true,
      onSuccess: () => {
        pushToast({ kind: "success", title: `Revoked invite to ${invite.email}` });
        onRevoked();
      },
      onError: (error) => setError(friendlyError(error)),
    },
  );
  return (
    <SettingsDialog
      title="Revoke this invitation"
      description="The invitation link will stop working immediately. You can send a new invitation later."
      onClose={onClose}
      busy={revoke.isPending}
      returnFocus={returnFocus}
    >
      <div className="member-dialog-person">
        <Identity email={invite.email} invited />
        <RoleChip role={invite.role} />
      </div>
      <InlineError error={error} />
      <div className="settings-dialog-actions">
        <Button variant="ghost" onClick={onClose} disabled={revoke.isPending}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={revoke.isPending}
          onClick={() => {
            if (revoke.isPending) return;
            setError(null);
            revoke.mutate();
          }}
        >
          {revoke.isPending ? "Working…" : "Revoke invite"}
        </Button>
      </div>
    </SettingsDialog>
  );
}
function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
